import { chatQueries } from "@/features/chat/api/chat-keys";
import { containerQueries } from "@/features/containers/api/containers-keys";
import { docsQueries } from "@/features/docs/api/docs-keys";
import { projectAccessQueries } from "@/features/projects/api/project-access-keys";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  CHAT_SHARED,
  DOCUMENT_SHARED,
  PROJECT_JOIN_APPROVED,
  PROJECT_JOIN_NOTIFICATION_PREFIX,
} from "../notifications.types";
import {
  createNotificationsSubscription,
  deleteAllReadNotifications,
  deleteNotification,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
  removeNotificationsSubscription,
} from "./notifications-api";
import { notificationQueries } from "./notifications-keys";

/**
 * CR-203 S3.5: a join-request notification means a request list, a discovery
 * card or (on approval) the requester's memberships changed — refresh them so
 * the owner's badge/review section and the requester's card update without a
 * reload. A null type (resubscribe after a possible gap, or a DELETE) refreshes
 * the cheap project-access keys only; membership lists wait for approval.
 */
export function invalidateProjectAccessOnNotification(
  queryClient: QueryClient,
  changedType: string | null,
): void {
  if (changedType !== null && !changedType.startsWith(PROJECT_JOIN_NOTIFICATION_PREFIX)) {
    return;
  }
  void queryClient.invalidateQueries({ queryKey: projectAccessQueries.all() });
  if (changedType === PROJECT_JOIN_APPROVED) {
    void queryClient.invalidateQueries({ queryKey: containerQueries.lists() });
    void queryClient.invalidateQueries({ queryKey: containerQueries.memberships() });
  }
}

/**
 * CR-263: a share notification means the recipient can now see a document or
 * a chat they could not before — refresh the sidebar / list pages so it shows
 * up without a reload. Only the list prefixes of the shared kind are touched.
 * A null type (resubscribe, DELETE) is ignored: the lists were fetched on
 * mount, and refetching them on every (re)subscribe would double the load.
 */
export function invalidateSharedListsOnNotification(
  queryClient: QueryClient,
  changedType: string | null,
): void {
  if (changedType === DOCUMENT_SHARED) {
    void queryClient.invalidateQueries({ queryKey: docsQueries.lists() });
    return;
  }
  if (changedType === CHAT_SHARED) {
    void queryClient.invalidateQueries({ queryKey: chatQueries.roomLists() });
    void queryClient.invalidateQueries({ queryKey: chatQueries.messageRoomLists() });
  }
}

export function useNotificationsQuery(userId: string) {
  return useQuery({
    ...notificationQueries.list(userId),
    enabled: !!userId,
  });
}

export function useNotificationsSubscription(userId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return;

    const channel = createNotificationsSubscription(userId, (changedType) => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueries.list(userId).queryKey,
      });
      invalidateProjectAccessOnNotification(queryClient, changedType);
      invalidateSharedListsOnNotification(queryClient, changedType);
    });

    return () => {
      removeNotificationsSubscription(channel);
    };
  }, [userId, queryClient]);
}

export function useMarkNotificationReadMutation(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(notificationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueries.all(userId),
      });
    },
  });
}

export function useMarkNotificationUnreadMutation(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => markNotificationUnread(notificationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueries.all(userId),
      });
    },
  });
}

export function useMarkAllNotificationsReadMutation(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markAllNotificationsRead(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueries.all(userId),
      });
    },
  });
}

export function useDeleteNotificationMutation(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => deleteNotification(notificationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueries.all(userId),
      });
    },
  });
}

export function useDeleteAllReadNotificationsMutation(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteAllReadNotifications(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueries.all(userId),
      });
    },
  });
}
